import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ResourceCrudPage, type ResourceListArgs, type PaginatedResult } from '@vipso/shared';
import {
  useListWarehousesQuery,
  useAddUpdateWarehouseMutation,
  useDeleteWarehouseMutation,
} from '../api/enhanceGenerated';
import { useModuleAccess } from '../hooks/useModuleAccess';
import { useApprovalModuleAccess } from '../hooks/useApprovalModuleAccess';

type WarehouseRow = {
  id: string;
  name: string;
  phone?: string;
  email?: string;
  approvalStatus?: string | null;
};

function useWarehouseList(args: ResourceListArgs) {
  const result = useListWarehousesQuery(args);
  const data: PaginatedResult<WarehouseRow> | undefined = result.data?.listWarehouses
    ? {
        ...result.data.listWarehouses,
        rows: result.data.listWarehouses.rows.map((r) => ({
          id: r.id,
          name: r.name,
          phone: r.phone ?? undefined,
          email: r.email ?? undefined,
          approvalStatus: (r as { approvalStatus?: string | null }).approvalStatus ?? null,
        })),
      }
    : undefined;
  return { ...result, data };
}

export function WarehousePage() {
  const { t } = useTranslation();
  const access = useModuleAccess('warehouse');
  const approvalAccess = useApprovalModuleAccess();
  const [save, { isLoading: saving }] = useAddUpdateWarehouseMutation();
  const [remove] = useDeleteWarehouseMutation();

  const fields = useMemo(
    () => [
      { key: 'name', label: t('common.name'), required: true as const },
      { key: 'phone', label: t('common.phone'), type: 'phone' as const },
      { key: 'email', label: t('common.email') },
      ...(approvalAccess.warehouse
        ? [
            {
              key: 'approvalStatus',
              label: t('approval.status'),
              form: false as const,
            },
          ]
        : []),
    ],
    [t, approvalAccess.warehouse],
  );

  return (
    <ResourceCrudPage<WarehouseRow>
      title={t('nav.warehouse')}
      description={t('warehouse.description')}
      entityLabel={t('warehouse.entity')}
      canCreate={access.canCreate}
      canUpdate={access.canUpdate}
      canDelete={access.canDelete}
      saving={saving}
      refreshLabel={t('common.refresh')}
      useList={useWarehouseList}
      fields={fields}
      onSave={(values, editing) =>
        save({
          input: {
            name: values.name,
            phone: values.phone,
            email: values.email,
            id: editing?.id,
          },
        }).unwrap()
      }
      onDelete={(row) => remove({ id: row.id }).unwrap()}
    />
  );
}
